/**
 * Dial · upgrade card.
 *
 * 5-tier paywall.
 *   Free                              → top-50 rotary + warmth + neglect
 *   Pro      ₹999/yr                  → relationship graph + voice memos + gift log + circle tags
 *   Couples  ₹1999/yr                 → shared spouse view
 *   Family   ₹2999/yr                 → 4 members
 *   Wealth   ₹14999/yr (B2B pilot)    → wealth-manager VIP-relationship console
 */
import { Payment, Storage } from '../chassis/index.ts';

const TIER_FILE = 'dial-tier.json';

interface TierState {
  readonly tier: Payment.TierId;
  readonly purchased_at: string | null;
  readonly payment_id: string | null;
}

async function loadTier(): Promise<TierState> {
  return (await Storage.readFile<TierState>(TIER_FILE)) ?? { tier: 'free', purchased_at: null, payment_id: null };
}
async function saveTier(t: TierState): Promise<void> {
  await Storage.writeFile(TIER_FILE, t);
}

export class DialUpgradeCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #state: TierState = { tier: 'free', purchased_at: null, payment_id: null };

  async connectedCallback(): Promise<void> {
    this.#state = await loadTier();
    this.#render();
  }

  async #checkout(tier: Exclude<Payment.TierId, 'free'>): Promise<void> {
    try {
      const res = await Payment.checkout(tier);
      if (!res.dismissed && res.paymentId) {
        const next: TierState = { tier, purchased_at: new Date().toISOString(), payment_id: res.paymentId };
        await saveTier(next);
        this.#state = next;
        this.#render();
      }
    } catch (e) {
      console.warn('checkout failed', e);
    }
  }

  #render(): void {
    const tier = this.#state.tier;
    const isPro = tier !== 'free';

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 14px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--maroon); font-weight: 700; letter-spacing: -0.005em; }
.lede { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-dim); text-wrap: pretty; max-width: 38ch; }
.grid { display: grid; gap: 10px; grid-template-columns: 1fr; }
@container page (min-width: 460px) { .grid { grid-template-columns: 1fr 1fr; } }
.card-tier {
  background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px;
  padding: 18px; display: grid; gap: 8px; position: relative; overflow: hidden; isolation: isolate;
}
.card-tier::before {
  content: ''; position: absolute; inset: 6px; border-radius: 4px; pointer-events: none;
  border: 1px solid color-mix(in oklch, var(--brass) 14%, transparent);
}
.name { font-family: var(--display); font-style: italic; font-size: 18px; color: var(--ink); font-weight: 700; }
.price { font-family: var(--display); font-size: 28px; font-weight: 700; color: var(--maroon); letter-spacing: -0.02em; line-height: 1; font-feature-settings: 'tnum' 1; }
.period { font-family: var(--mono); font-size: 10px; letter-spacing: 0.32em; text-transform: uppercase; color: var(--ink-faint); }
ul { font-family: var(--display); font-size: 14px; color: var(--ink-dim); list-style: none; padding: 0; margin: 6px 0 12px; display: grid; gap: 4px; }
ul li::before { content: '·'; margin-right: 6px; color: var(--maroon); font-weight: 700; }
.cta {
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.36em; text-transform: uppercase;
  padding: 12px 16px; background: linear-gradient(180deg, var(--maroon), var(--maroon-deep));
  color: var(--paper); border-radius: 6px; font-weight: 700; cursor: pointer; border: 0;
}
.cta[disabled] { opacity: 0.5; cursor: default; filter: grayscale(0.4); }
.card-tier.is-current { border-color: var(--maroon); border-left: 3px solid var(--maroon); }
.refund { font-family: var(--mono); font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ink-faint); margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--rule); text-align: center; }
</style>
<header class="head">
  <span class="eyebrow">Membership · empire imprint</span>
  <h3 class="title">${isPro ? `Pro tier locked · ${tier}` : 'Dial Pro pe upgrade karein.'}</h3>
  <p class="lede">Dial Pro adds the relationship-health graph, voice memos, gift log + circle tags. Couples mode includes Naina-style shared health. Wealth tier is a B2B pilot for VIP wealth managers.</p>
</header>
<div class="grid">
  <div class="card-tier ${tier === 'pro' ? 'is-current' : ''}">
    <div class="name">Pro</div>
    <div class="price">₹999<span class="period"> / yr</span></div>
    <ul>
      <li>Health × reciprocity graph</li>
      <li>Voice memos per contact</li>
      <li>Gift log</li>
      <li>Multi-circle tags</li>
    </ul>
    <button class="cta" data-tier="pro" ${tier === 'pro' ? 'disabled' : ''}>${tier === 'pro' ? 'Active' : 'Get Pro'}</button>
  </div>
  <div class="card-tier ${tier === 'couple' ? 'is-current' : ''}">
    <div class="name">Couples</div>
    <div class="price">₹1,999<span class="period"> / yr</span></div>
    <ul>
      <li>Shared partner view</li>
      <li>Family-circle co-managed</li>
      <li>Pro features × 2</li>
      <li>Anniversaries auto-synced</li>
    </ul>
    <button class="cta" data-tier="couple" ${tier === 'couple' ? 'disabled' : ''}>${tier === 'couple' ? 'Active' : 'Get Couples'}</button>
  </div>
</div>
<div class="refund">30-day money-back · DPDP-compliant · cancel via aditya@taxwalaai.com</div>
`;

    this.#shadow.querySelectorAll<HTMLButtonElement>('.cta').forEach((b) => {
      b.addEventListener('click', () => {
        const t = b.dataset.tier as Exclude<Payment.TierId, 'free'>;
        if (t && !b.hasAttribute('disabled')) void this.#checkout(t);
      });
    });
  }
}

customElements.define('dial-upgrade-card', DialUpgradeCard);
